/* eslint-disable no-unused-vars */
import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import dayjs from "dayjs"
import { Box } from "@mui/material"
import PackagingExecutionHeader from "./PackagingExecutionHeader"
import GenericHeaderReturnButton from "../../components/GenericHeaderReturnButton"
import { getPackagingExecutionDateSelector, getPackagingExecutionSiteSelector, getPackagingExecutionsSelector, getPEsSidebarNotificationCountsSelector, getSelectedPEsSidebarMenuSelector, getSelectedPEsSidebarTabSelector } from "../../reducers/Packaging/packagingExecution"
import { PE_STATUSES } from "../../utils/packagingExecutions"
import { showPackagingExecution, showPackagingExecutionFilter, showPackagingExecutionsHoursEdit, updatedSectionsCounterWeight } from "../../actions/PackagingExecution/packagingExecution"
import { getPlanningRules } from "../../reducers/Misc/misc"
import { DEFAULT_SELECTED_PES_SIDEBAR_MENU, PES_SIDEBAR_MENUS_ENUM, PES_SIDEBAR_MENUS_LABELS } from "../../utils/packagingExecutionUtils"
import OutputIcon from "./icons/OutputIcon"
import WeightControlIcon from "./icons/WeightControlIcon"
import TastingIcon from "./icons/TastingIcon"
import ToPackIcon from "./icons/ToPackIcon"
import ExecutionsSidebar from "../Executions/ExecutionsSidebar"
import PackagingExecutionTableNew from "./PackagingExecutionTableNew"
import MoreMenu from "../../components/MoreMenu"
import RecipeDialogForm from "./counterWeighing/RecipeDialogForm"
import SectionsSelectionDialogForm from "./counterWeighing/SectionsSelectionDialogForm"
import PreparedPackagingDialogForm from "./counterWeighing/PreparedPackagingDialogForm"
import PEReprodDialogForm from "./reprod/PEReprodDialogForm"
import PEReprodConfirmationDialog from "./reprod/PEReprodConfirmationDialog"
import { generatePackagingExecutionReprodBySections } from "../../actions/ProductionSteps/Reproduction"

const menus = [
	{
		id: PES_SIDEBAR_MENUS_ENUM.WEIGHT_CONTROL,
		label: PES_SIDEBAR_MENUS_LABELS[PES_SIDEBAR_MENUS_ENUM.WEIGHT_CONTROL],
		icon: WeightControlIcon,
		tabs: [
			{ value: PE_STATUSES.toValidate, label: "À valider", filter: (pe) => pe.status === PE_STATUSES.toValidate },
			{ value: PE_STATUSES.locked, label: "Verrouillées", filter: (pe) => pe.status === PE_STATUSES.locked },
		],
	},
	{
		id: PES_SIDEBAR_MENUS_ENUM.TO_PACK,
		label: PES_SIDEBAR_MENUS_LABELS[PES_SIDEBAR_MENUS_ENUM.TO_PACK],
		icon: ToPackIcon,
		tabs: [
			{ value: PE_STATUSES.todo, label: "À faire", filter: (pe) => pe.status === PE_STATUSES.todo },
			{ value: PE_STATUSES.inProgress, label: "En cours", filter: (pe) => pe.status === PE_STATUSES.inProgress || pe.status === PE_STATUSES.packagingsCounting || pe.status === PE_STATUSES.waste },
			{ value: PE_STATUSES.done, label: "Terminées", filter: (pe) => pe.status === PE_STATUSES.done }
		]
	},
	// {
	// 	id: PES_SIDEBAR_MENUS_ENUM.TASTING,
	// 	label: PES_SIDEBAR_MENUS_LABELS[PES_SIDEBAR_MENUS_ENUM.TASTING],
	// 	icon: TastingIcon,
	// 	tabs: [
	// 		{ value: PE_STATUSES.toTaste, label: "À goûter", filter: (pe) => pe.status === PE_STATUSES.toTaste }
	// 	]
	// },
	// {
	// 	id: PES_SIDEBAR_MENUS_ENUM.OUTPUT,
	// 	label: PES_SIDEBAR_MENUS_LABELS[PES_SIDEBAR_MENUS_ENUM.OUTPUT],
	// 	icon: OutputIcon,
	// 	tabs: []
	// },
]

const PackagingExecutionRead = () => {
	const dispatch = useDispatch()
	const selectedSite = useSelector(getPackagingExecutionSiteSelector)
	const selectedDate = useSelector(getPackagingExecutionDateSelector)
	const packagingExecutions = useSelector(getPackagingExecutionsSelector)
	const planningRules = useSelector(getPlanningRules)

	const [selectedPackagingExecution, setSelectedPackagingExecution] = useState(null)

	const selectedMenuFromStore = useSelector(getSelectedPEsSidebarMenuSelector)
	const selectedTabFromStore = useSelector(getSelectedPEsSidebarTabSelector)
	const [selectedMenu, setSelectedMenu] = useState(selectedMenuFromStore || DEFAULT_SELECTED_PES_SIDEBAR_MENU)
	const [isSidebarExpanded, setIsSidebarExpanded] = useState(true)	// 1. first dialog (recipe)
	const [isOpenRecipeDialog, setIsOpenRecipeDialog] = useState(false)
	// 2. second dialog (sections counter weight)
	const [isOpenSectionsDialog, setIsOpenSectionsDialog] = useState(false)
	// 3. third dialog (prepared packaging) (only for inProgress status)
	const [isOpenPreparedPackagingDialog, setIsOpenPreparedPackagingDialog] = useState(false)
	// 4. fourth dialog (reprod)
	const [isOpenReprodDialog, setIsOpenReprodDialog] = useState(false)
	// 5. fifth dialog (reprod confirmation)
	const [isOpenReprodConfirmationDialog, setIsOpenReprodConfirmationDialog] = useState(false)
	const [peFormValues, setPEFormValues] = useState(null)

	const [commentModalShown, setCommentModalShown] = useState(false)
	const [selectedComments, setSelectedComments] = useState([])

	const toggleOpenRecipeDialog = () => setIsOpenRecipeDialog(!isOpenRecipeDialog)
	const toggleOpenPreparedPackagingDialog = () => setIsOpenPreparedPackagingDialog(!isOpenPreparedPackagingDialog)
	const toggleOpenReprodDialog = () => setIsOpenReprodDialog(!isOpenReprodDialog)
	const toggleOpenReprodConfirmationDialog = () => setIsOpenReprodConfirmationDialog(!isOpenReprodConfirmationDialog)

	// 1. recipe selection form
	const handleSubmitRecipe = (values) => {
		setSelectedPackagingExecution(values.recipe)
		setIsOpenSectionsDialog(true)
	}

	// 2. sections counter weight form
	const handleSubmitSections = async (values) => {
		if (!selectedPackagingExecution) return
		await dispatch(updatedSectionsCounterWeight(selectedPackagingExecution.objectId, values))
		setIsOpenSectionsDialog(false)

		// open 3th dialog (prepared packaging) if status is inProgress
		if (selectedPackagingExecution?.status === PE_STATUSES.inProgress) {
			toggleOpenPreparedPackagingDialog()
			return
		}

		// open 4th dialog (reprod) if status is other than inProgress
		setIsOpenReprodDialog(true)
	}

	// 3. prepared packaging form (only for inProgress status)
	const handleSubmitPreparedPackaging = async (values) => {
		if (selectedPackagingExecution) {
			setSelectedPackagingExecution(prev => ({ ...prev, realizedNumber: values.realizedNumber }))
		}
		setIsOpenRecipeDialog(false)

		// open 4th dialog (reprod) if status is other than inProgress
		setIsOpenReprodDialog(true)
	}

	// 4. reprod form
	const handleSubmitReprod = (values) => {
		if (!selectedPackagingExecution) return
		setPEFormValues(values)
		setIsOpenReprodConfirmationDialog(true)

		setIsOpenReprodDialog(false)
	}

	// 5. reprod confirmation
	const handleConfirmReprod = async () => {
		await dispatch(generatePackagingExecutionReprodBySections(peFormValues))
		setIsOpenReprodConfirmationDialog(false)
	}
	const showComments = (comments) => {
		// console.log("to be continued", commentModalShown, selectedComments)
		//@TODO copied from PackagingExecutionTable, should be moved to a more generic place if needed for other features

		setSelectedComments(comments)
		setCommentModalShown(true)
	}

	const handleCloseSectionsDialog = () => {
		setIsOpenSectionsDialog(false)
		toggleOpenRecipeDialog()
	}

	const onClickPe = (pe) => {
		dispatch(showPackagingExecution(selectedSite.objectId, selectedDate, pe.id))
	}

	const PETiles = packagingExecutions.map(pe => ({
		...pe,
		...menus.reduce((acc, menu) => {
			acc[menu.id] = menu.tabs.some(tab => tab.filter(pe))
			return acc
		}, {})
	}))

	const notificationCounts = PETiles.reduce((acc, tile) => {
		for (const menu of menus) {
			if (tile[menu.id]) {
				acc[menu.id] = {
					...acc[menu.id],
					[tile.status]: (acc[menu.id]?.[tile.status] || 0) + 1,
					total: (acc[menu.id]?.total || 0) + 1,
				}
			}
		}
		return acc
	}, {})



	const goToPackagingExecutionHoursEdit = () => {
		// return // TODO get status from selected menu/tab
		const localStorage = window.localStorage
		localStorage.setItem("packagingExecutionStatus", "TODO")
		dispatch(showPackagingExecutionsHoursEdit(selectedSite.objectId, selectedDate))
	}

	return (
		<Box sx={{ height: "100%", minHeight: 0 }}>
			<PackagingExecutionHeader
				leftAction={<GenericHeaderReturnButton handleClick={() => {
					dispatch({ type: "CLEAR_PES_SELECTED_SIDEBAR" })
					dispatch(showPackagingExecutionFilter())
				}} />}
				subtitle={`${selectedSite.name} - ${dayjs(selectedDate).format("DD/MM/YYYY")}`}
				fixedHeader
				rightAction={
					<MoreMenu menus={[{
						label: "Éditer les heures et les lignes de barquettage",
						onClick: goToPackagingExecutionHoursEdit
					},
					{
						label: "Faire une contre-pesée/reprod",
						onClick: toggleOpenRecipeDialog
					}]} />
				}
			/>
			<Box sx={{ display: "flex", flexDirection: "row", height: "100%", minHeight: 0, paddingTop: "64px" }}>
				<ExecutionsSidebar
					selected={selectedMenu}
					onSelect={(menuId) => {
						setSelectedMenu(menuId)
						dispatch({ type: "LOAD_PES_SELECTED_SIDEBAR", selectedSidebar: menuId })
					}}
					isExpanded={isSidebarExpanded}
					onToggleExpand={() => setIsSidebarExpanded(prev => !prev)}
					notifications={notificationCounts}
					menus={menus}
				/>
				<PackagingExecutionTableNew
					PackagingTiles={PETiles.filter(tile => tile[selectedMenu])}
					selectedMenu={selectedMenu}
					tabs={menus.find(menu => menu.id === selectedMenu)?.tabs || []}
					selectedTab={selectedTabFromStore}
					onTabChange={(tab) => dispatch({ type: "LOAD_PES_SELECTED_TAB", selectedTab: tab })}
					site={selectedSite}
					date={selectedDate}
				// showComments={showComments} @TODO to be implemented when comments will be supported for packaging executions
				/>
			</Box>
			{/* 1. recipe selection dialog */}
			<RecipeDialogForm
				onClose={toggleOpenRecipeDialog}
				open={isOpenRecipeDialog}
				onSubmit={handleSubmitRecipe}
			/>
			{/* 2. sections modal */}
			<SectionsSelectionDialogForm
				packagingExecution={selectedPackagingExecution}
				open={isOpenSectionsDialog}
				onClose={handleCloseSectionsDialog}
				onSubmit={handleSubmitSections}
			/>
			{/* 3. prepared packaging form */}
			<PreparedPackagingDialogForm
				open={isOpenPreparedPackagingDialog}
				onClose={toggleOpenPreparedPackagingDialog}
				packagingExecution={selectedPackagingExecution}
				onSubmit={handleSubmitPreparedPackaging}
			/>
			{/* 4. reprod dialog */}
			<PEReprodDialogForm
				packagingExecution={selectedPackagingExecution}
				open={isOpenReprodDialog}
				onClose={toggleOpenReprodDialog}
				onSubmit={handleSubmitReprod}
				planningRules={planningRules}
			/>
			{/* 5. reprod confirmation dialog */}
			<PEReprodConfirmationDialog
				open={isOpenReprodConfirmationDialog}
				onClose={toggleOpenReprodConfirmationDialog}
				onConfirm={handleConfirmReprod}
				sections={peFormValues?.sections}
			/>
		</Box>
	)
}

export default PackagingExecutionRead